import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useAuthContext } from 'src/auth/useAuthContext';
import { useUserContext } from 'src/auth/useUserContext';
import { suggestionApi } from 'src/api/suggestion';
import { CustomAvatar } from 'src/components/custom-avatar';
import { getFileUrl } from 'src/utils/getFileUrl';
import { markdownToEditorHtml } from 'src/utils/markdownToEditorHtml';
import Iconify from 'src/components/iconify/Iconify';
import Markdown from 'src/components/markdown';
import { PetConversationResponseDtoDocumentSourcesInner } from 'woofwoof-api';
import {
  AssistantChatCacheData,
  clearAssistantChatCache,
  getAssistantChatCache,
  pruneAssistantChatCache,
  setAssistantChatCache,
} from 'src/sections/@dashboard/visit/utils/assistantChatCache';
import {
  AssistantAction,
  parseAssistantAction,
} from 'src/sections/@dashboard/visit/utils/assistantActions';
import { translateKey } from 'src/locales/translate';
import { useAssistantSummaryQuery } from 'src/sections/@dashboard/profile/hooks/useAssistantSummaryQuery';
import { useStorageIdentity } from 'src/sections/@dashboard/organization/context/OrganizationStorageBoundary';

// Applied via `sx` (not styled(Markdown)) because react-markdown v8 dropped the
// className prop, so styled(Markdown)'s generated class never reaches the
// markdown — only `sx` is forwarded to the styled root that carries the base
// styles. The base spreads body1 onto ul/ol and sets li line-height to 2, which
// made bulleted sections render larger/looser than plain paragraphs; these
// overrides land on the same element and (injected later) win, matching body2.
const assistantMarkdownSx: SxProps<Theme> = (theme) => ({
  '& p, & ul, & ol': {
    marginTop: 0,
    marginBottom: theme.spacing(1),
    fontSize: theme.typography.body2.fontSize,
  },
  // The shared Markdown component renders headings as
  // `<Typography component="span" variant="hN">`, i.e. `<span class="MuiTypography-hN">`,
  // so plain `& h2` tag selectors never match. Target the MUI variant classes
  // instead and scale the otherwise huge default h1/h2 down to a readable in-chat
  // size with light hierarchy.
  '& .MuiTypography-h1, & .MuiTypography-h2, & .MuiTypography-h3, & .MuiTypography-h4, & .MuiTypography-h5, & .MuiTypography-h6':
    {
      display: 'block',
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(0.75),
      lineHeight: 1.3,
      fontWeight: 700,
    },
  '& .MuiTypography-h1': { fontSize: '1.15rem' },
  '& .MuiTypography-h2': { fontSize: '1.05rem' },
  '& .MuiTypography-h3': { fontSize: '1rem' },
  '& .MuiTypography-h4, & .MuiTypography-h5, & .MuiTypography-h6': {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: 600,
  },
  '& strong': { fontWeight: 600 },
  '& ul, & ol': { paddingLeft: theme.spacing(3) },
  '& ul li, & ol li': {
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight,
  },
  '& > *:last-child': { marginBottom: 0 },
});

const AssistantMarkdown = ({ children }: { children: string }) => (
  <Markdown sx={assistantMarkdownSx}>{children}</Markdown>
);

interface ProfessionalAssistantChatProps {
  profileId: string;
  profileName: string;
  visitId?: string;
  initialContentOverride?: string;
  /**
   * Fill the parent's height instead of capping the message list at 60vh — for
   * the side panel in the visit modal, where the chat should fill the column
   * and scroll within it. Default false keeps the standalone-page behaviour.
   */
  fillHeight?: boolean;
  /**
   * Natural chat order — input at the bottom, messages oldest→newest (newest at
   * the bottom) with auto-scroll. Default false keeps the original layout (input
   * on top, newest-first). Used by the visit modal only.
   */
  naturalOrder?: boolean;
  /**
   * Render the message scrollbar in the surrounding container's existing gutter
   * (at the panel edge) instead of over the right-aligned avatars. Only safe
   * when the chat is wrapped by a container that HAS a horizontal gutter — see
   * {@link edgeScrollbarGutter}. Default false.
   */
  edgeScrollbar?: boolean;
  /**
   * The wrapper's horizontal gutter, in spacing units, that `edgeScrollbar`
   * pulls into. It must match that gutter: the scroll viewport is widened by
   * exactly this much, so a larger value leaves the viewport hanging past the
   * panel's edge and the panel scrolls sideways. Defaults to 3 (24px).
   */
  edgeScrollbarGutter?: number;
  /**
   * Current Notatki dla pacjenta editor content, sent with each follow-up so the
   * assistant can transform it in place. Modal-only; omitted elsewhere.
   */
  patientNotesDraft?: string;
  /**
   * Current Notatki wewnętrzne editor content, sent so the assistant can read
   * what the professional is writing. Modal-only; omitted elsewhere.
   */
  internalNotesDraft?: string;
  /**
   * Called when a follow-up response carries an action (e.g. fill the patient
   * notes). Modal-only; omitted elsewhere.
   */
  onAssistantAction?: (action: AssistantAction) => void;
  /**
   * Surface hint for the backend. Pass 'patient_note' from the text-note modal
   * to enable get_text_notes + fill_text_note. Omitted elsewhere.
   */
  surface?: string;
  /**
   * Current text-note editor content (HTML), sent with each follow-up so the
   * assistant can read/transform the open note. Modal-only; omitted elsewhere.
   */
  textNoteDraft?: string;
}

interface ResponseData {
  content: string;
  generatedAt?: string;
  differentialDiagnoses?: string[] | null;
  suggestedTests?: string[] | null;
  treatmentRecommendations?: string[] | null;
  warnings?: string[] | null;
  documentSources?: PetConversationResponseDtoDocumentSourcesInner[] | null;
}

interface Exchange {
  question: string;
  response: ResponseData;
  /** When the question was asked (ISO) — shown as the bubble time label. */
  askedAt?: string;
}

interface Exchange {
  question: string;
  response: ResponseData;
  /** When the question was asked (ISO) — shown as the bubble time label. */
  askedAt?: string;
}

interface ConversationMessage {
  role: 'professional' | 'assistant';
  content: string;
}

const ALL_PLACEHOLDER_TEXTS = [
  translateKey('hardcoded.jakie_sa_mozliwe_diagnozy_dla_tych_objawow'),
  'Jakie testy diagnostyczne polecasz?',
  translateKey('hardcoded.czy_sa_jakies_interakcje_z_obecnymi_lekami'),
  translateKey('hardcoded.jakie_sa_zalecenia_dotyczace_leczenia'),
  translateKey('hardcoded.czy_sa_jakies_ostrzezenia_lub_przeciwwskazania'),
  translateKey('hardcoded.jakie_sa_typowe_powiklania_w_tym_przypadku'),
  'Czy pacjent wymaga hospitalizacji?',
  translateKey('hardcoded.jakie_sa_wskazania_do_konsultacji_specjalistycznej'),
  translateKey('hardcoded.jakie_parametry_nalezy_monitorowac'),
  translateKey('hardcoded.czy_sa_alternatywne_metody_leczenia'),
];

const formatChatTime = (value?: string | Date | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleTimeString('pl-PL', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
};

export default function ProfessionalAssistantChat({
  profileId,
  visitId,
  initialContentOverride,
  fillHeight = false,
  naturalOrder = false,
  edgeScrollbar = false,
  edgeScrollbarGutter = 3,
  patientNotesDraft,
  internalNotesDraft,
  onAssistantAction,
  surface,
  textNoteDraft,
}: ProfessionalAssistantChatProps) {
  const theme = useTheme();
  const [question, setQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [initialSummary, setInitialSummary] = useState<ResponseData | null>(
    null,
  );
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [pendingAskedAt, setPendingAskedAt] = useState<string | null>(null);
  const [conversationHistory, setConversationHistory] = useState<
    ConversationMessage[]
  >([]);
  const [placeholder, setPlaceholder] = useState('');
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [cycleCount, setCycleCount] = useState(0);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const loadingMessages = [
    translateKey('petAi.loading1'),
    translateKey('petAi.loading2'),
    translateKey('petAi.loading3'),
    translateKey('petAi.loading4'),
    translateKey('petAi.loading5'),
  ];
  const [selectedTexts] = useState(() => {
    // Select 3 random questions from the pool
    const shuffled = [...ALL_PLACEHOLDER_TEXTS].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 3);
  });
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();
  const { data: userData } = useUserContext();
  const storageScope = useStorageIdentity();
  const storageScopeRef = useRef(storageScope);
  storageScopeRef.current = storageScope;
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Animated typing placeholder effect
  useEffect(() => {
    // Stop after 3 cycles
    if (cycleCount >= 3) {
      return;
    }

    let charIndex = 0;
    let isDeleting = false;
    let timeoutId: NodeJS.Timeout;

    const type = () => {
      const currentText = selectedTexts[currentTextIndex];

      if (!isDeleting) {
        // Typing
        setPlaceholder(currentText.substring(0, charIndex + 1));
        charIndex++;

        if (charIndex === currentText.length) {
          // Finished typing, wait before deleting
          timeoutId = setTimeout(() => {
            isDeleting = true;
            type();
          }, 2000);
          return;
        }

        timeoutId = setTimeout(type, 50);
      } else {
        // Deleting
        setPlaceholder(currentText.substring(0, charIndex - 1));
        charIndex--;

        if (charIndex === 0) {
          // Finished deleting
          isDeleting = false;

          // Check if we've completed all 3 texts
          if (currentTextIndex === selectedTexts.length - 1) {
            // Completed all 3, stop animation
            setCycleCount(3);
            setPlaceholder(selectedTexts[selectedTexts.length - 1]);
            return;
          }

          // Move to next text
          setCurrentTextIndex((prev) => prev + 1);
          setCycleCount((prev) => prev + 1);
          timeoutId = setTimeout(type, 500);
          return;
        }

        timeoutId = setTimeout(type, 30);
      }
    };

    timeoutId = setTimeout(type, 500);

    return () => clearTimeout(timeoutId);
  }, [currentTextIndex, cycleCount, selectedTexts]);

  const persistCache = (
    summary: ResponseData,
    newExchanges: Exchange[],
    history: ConversationMessage[],
  ) => {
    // Persisted for both visit-pinned chats and the profile-wide patient chat.
    if (!storageScope || (!profileId && !visitId)) return;
    const cacheData: AssistantChatCacheData = {
      initialSummary: summary,
      exchanges: newExchanges,
      conversationHistory: history,
    };
    setAssistantChatCache(storageScope, profileId, visitId, cacheData);
  };

  const { data: aiSummaryData } = useAssistantSummaryQuery(profileId, visitId);

  const cacheRestoredRef = useRef(false);
  const prevGeneratedAtRef = useRef<string | null>(null);
  // True once a non-empty conversation has been restored from cache. The
  // AI-summary poll must never wipe it (the server's generatedAt differs from the
  // cached one). Set synchronously in the restore effect so it is reliable even
  // when react-query serves the summary from a warm cache on the same mount tick.
  const restoredConversationRef = useRef(false);

  // Drop expired persisted threads so localStorage doesn't grow without bound.
  useEffect(() => {
    pruneAssistantChatCache();
  }, []);

  useEffect(() => {
    // Persist whenever there's an identifier: a pinned visit (visit page /
    // modal) OR the profile-wide chat (patient page, profileId only).
    if (cacheRestoredRef.current || (!profileId && !visitId)) return;
    const cached = storageScope
      ? getAssistantChatCache(storageScope, profileId, visitId)
      : null;
    if (cached) {
      setInitialSummary(cached.initialSummary);
      setExchanges(cached.exchanges);
      setConversationHistory(cached.conversationHistory);
      setIsInitialLoading(false);
      // Anchor the "new summary" check to the restored summary so the AI-summary
      // poll below doesn't treat it as new and wipe the restored conversation
      // (the profile page resets the chat only on a genuinely newer summary).
      prevGeneratedAtRef.current = cached.initialSummary.generatedAt ?? null;
      restoredConversationRef.current = (cached.exchanges?.length ?? 0) > 0;
    } else if (initialContentOverride) {
      // Guest-visit path: no profile-based AI-summary query runs, so seed the
      // initial assistant message from the override (typically visit.aiNote).
      const summary: ResponseData = { content: initialContentOverride };
      setInitialSummary(summary);
      setConversationHistory([
        { role: 'assistant', content: initialContentOverride },
      ]);
      setIsInitialLoading(false);
    }
    cacheRestoredRef.current = true;
  }, [profileId, visitId, initialContentOverride, storageScope]);

  useEffect(() => {
    if (!aiSummaryData?.data) return;

    const summaryData = aiSummaryData.data as Record<string, unknown>;
    const summary: ResponseData = {
      content: (summaryData.response as string) || '',
      generatedAt: summaryData.generatedAt as string,
      differentialDiagnoses: summaryData.differentialDiagnoses as string[],
      suggestedTests: summaryData.suggestedTests as string[],
      treatmentRecommendations:
        summaryData.treatmentRecommendations as string[],
      warnings: summaryData.warnings as string[],
    };

    // A visit with documents still being processed returns an empty summary.
    // Keep the current chat while the query waits for its completed summary.
    if (
      !summary.content.trim() ||
      summary.content.includes('[GEMINI_DISABLED]')
    ) {
      return;
    }

    const generatedAt = summary.generatedAt || '';
    const isNewSummary = generatedAt !== prevGeneratedAtRef.current;

    setInitialSummary(summary);
    setIsInitialLoading(false);

    if (
      !visitId &&
      isNewSummary &&
      !restoredConversationRef.current &&
      exchanges.length === 0
    ) {
      // Profile page: (re)seed the conversation from a new summary ONLY when no
      // chat exists yet. Never wipe an existing/restored conversation — the
      // server returns a different generatedAt than the one cached from the last
      // session, so the old "isNewSummary" reset was erasing persisted chat
      // history on every reload. (restoredConversationRef covers the warm-cache
      // mount race where `exchanges` is still the stale pre-restore []; the
      // length check covers a chat built up during this session.) Explicit reset
      // stays on "Rozpocznij nowy czat".
      const history: ConversationMessage[] = [
        { role: 'assistant', content: summary.content },
      ];
      setConversationHistory(history);
      setExchanges([]);
    } else if (!conversationHistory.length) {
      const history: ConversationMessage[] = [
        { role: 'assistant', content: summary.content },
      ];
      setConversationHistory(history);
    }

    prevGeneratedAtRef.current = generatedAt;

    if (profileId || visitId) {
      persistCache(summary, exchanges, conversationHistory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiSummaryData]);

  useEffect(() => {
    if (!isLoading) {
      setLoadingMessageIndex(0);
      return;
    }

    const intervalId = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 1800);

    return () => clearInterval(intervalId);
  }, [isLoading, loadingMessages.length]);

  // Keep the newest message in view as content arrives, whichever end it is on:
  // natural order grows downward, so pin to the bottom; the default layout puts
  // the newest exchange at the TOP, where a professional who had scrolled back
  // through history would otherwise never see their answer arrive. Smooth there
  // because it moves the reader away from where they were looking, so the jump
  // needs to be legible; instant at the bottom, where rapid streaming updates
  // would otherwise fight an animation.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (naturalOrder) {
      el.scrollTop = el.scrollHeight;
    } else if (el.scrollTop > 0) {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [
    naturalOrder,
    exchanges.length,
    pendingQuestion,
    initialSummary?.content,
    isInitialLoading,
  ]);

  const handleSubmitQuestion = async () => {
    if (!question.trim()) {
      return;
    }

    const currentQuestion = question;
    // The answer belongs to the context that asked. If this chat unmounted or
    // the context changed meanwhile, drop it rather than persist or show it.
    const requestScope = storageScope;
    const isStale = () =>
      !isMountedRef.current || storageScopeRef.current !== requestScope;
    const askedAt = new Date().toISOString();
    setQuestion('');
    setPendingQuestion(currentQuestion);
    setPendingAskedAt(askedAt);
    setIsLoading(true);

    try {
      const response = await suggestionApi.professionalAssistantFollowup({
        profileId,
        visitId,
        currentQuestion,
        conversationHistory: conversationHistory,
        patientNotesDraft,
        internalNotesDraft,
        surface,
        textNoteDraft,
      });
      if (isStale()) return;

      // When the assistant performs an action (e.g. fills the Notatki dla pacjenta
      // editor), show a deterministic confirmation in the chat instead of the
      // model's free-text reply (which can otherwise be a long summary) and drop
      // the clinical sub-fields so the bubble stays a clean confirmation. Only in
      // contexts that actually handle the action — the visit modal passes
      // onAssistantAction; elsewhere the model's answer is kept untouched.
      const action = parseAssistantAction(response.data.action);
      const performedAction = !!action && !!onAssistantAction;
      const assistantContent = performedAction
        ? action!.type === 'fill_text_note'
          ? translateKey('hardcoded.notatka_tekstowa_dodana_potwierdzenie')
          : translateKey('hardcoded.notatki_dodane_do_wizyty_potwierdzenie')
        : response.data.response;

      const newExchange: Exchange = {
        question: currentQuestion,
        askedAt,
        response: {
          content: assistantContent,
          differentialDiagnoses: performedAction
            ? null
            : response.data.differentialDiagnoses,
          suggestedTests: performedAction ? null : response.data.suggestedTests,
          treatmentRecommendations: performedAction
            ? null
            : response.data.treatmentRecommendations,
          warnings: performedAction ? null : response.data.warnings,
          documentSources: performedAction
            ? null
            : response.data.documentSources,
        },
      };

      const updatedExchanges = [...exchanges, newExchange];
      setExchanges(updatedExchanges);

      const updatedHistory: ConversationMessage[] = [
        ...conversationHistory,
        { role: 'professional', content: currentQuestion },
        { role: 'assistant', content: assistantContent },
      ];
      setConversationHistory(updatedHistory);

      setPendingQuestion(null);

      if (action) {
        onAssistantAction?.(action);
      }

      if (initialSummary) {
        persistCache(initialSummary, updatedExchanges, updatedHistory);
      }
    } catch (error) {
      if (isStale()) return;
      console.error('Error submitting question:', error);
      enqueueSnackbar(
        translateKey('hardcoded.nie_udalo_sie_wyslac_pytania_sprobuj_ponownie'),
        {
          variant: 'error',
        },
      );
      setPendingQuestion(null);
      setPendingQuestion(null);
      setQuestion(currentQuestion);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmitQuestion();
    }
  };

  // Erase the persisted thread and reset to a fresh conversation — keeps the
  // patient/visit overview (initial summary) and wipes the Q&A so the
  // professional can start over.
  const handleNewChat = () => {
    if (storageScope) {
      clearAssistantChatCache(storageScope, profileId, visitId);
    }
    setExchanges([]);
    setPendingQuestion(null);
    setQuestion('');
    setConversationHistory(
      initialSummary
        ? [{ role: 'assistant', content: initialSummary.content }]
        : [],
    );
  };

  if (isInitialLoading) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 5,
          // `flex: 1` rather than `height: '100%'`: the panel around this gets
          // its size from a min-height, against which a percentage height
          // resolves to auto — which left this state hugging the top of an
          // otherwise empty panel. Growing to fill centres it the way the
          // transcript tab's empty state already does.
          flex: 1,
          minHeight: 320,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            mb: 3,
          }}
        >
          <Box
            component="img"
            src="/assets/logo/logo_symbol.svg"
            alt="AI Analysis"
            sx={{
              height: 80,
              animation: 'bounce 2s infinite',
              '@keyframes bounce': {
                '0%, 100%': {
                  transform: 'translateY(0)',
                },
                '50%': {
                  transform: 'translateY(-10px)',
                },
              },
            }}
          />
        </Box>

        <Typography variant="h5" sx={{ mb: 1 }}>
          Analizujemy profil pacjenta
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {translateKey(
            'hardcoded.generujemy_podsumowanie_kliniczne_wkrotce_otrzymasz_szczegolowa_analize',
          )}
        </Typography>
      </Box>
    );
  }

  // Helper function to render structured data
  const renderStructuredData = (response: ResponseData) => (
    <Stack
      spacing={2}
      sx={{
        mt: 2,
        pt: 2,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      {response.differentialDiagnoses &&
        response.differentialDiagnoses.length > 0 && (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {translateKey('hardcoded.diagnoza_roznicowa')}
            </Typography>
            <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0 }}>
              {response.differentialDiagnoses.map(
                (diagnosis: string, idx: number) => (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body2"
                    sx={{ mb: 0.5 }}
                  >
                    {diagnosis}
                  </Typography>
                ),
              )}
            </Box>
          </Box>
        )}

      {response.suggestedTests && response.suggestedTests.length > 0 && (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Zalecane badania:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0 }}>
            {response.suggestedTests.map((test: string, idx: number) => (
              <Typography
                key={idx}
                component="li"
                variant="body2"
                sx={{ mb: 0.5 }}
              >
                {test}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {response.treatmentRecommendations &&
        response.treatmentRecommendations.length > 0 && (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Rekomendacje leczenia:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0 }}>
              {response.treatmentRecommendations.map(
                (rec: string, idx: number) => (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body2"
                    sx={{ mb: 0.5 }}
                  >
                    {rec}
                  </Typography>
                ),
              )}
            </Box>
          </Box>
        )}

      {response.warnings && response.warnings.length > 0 && (
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, mb: 1, color: theme.palette.error.main }}
          >
            {translateKey('hardcoded.ostrzezenia')}
          </Typography>
          <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0 }}>
            {response.warnings.map((warning: string, idx: number) => (
              <Typography
                key={idx}
                component="li"
                variant="body2"
                sx={{ mb: 0.5, color: theme.palette.error.main }}
              >
                {warning}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {response.documentSources &&
        response.documentSources.length > 0 &&
        (() => {
          const uniqueSources = response.documentSources.filter(
            (source, index, self) =>
              index === self.findIndex((s) => s.filename === source.filename),
          );
          return (
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Iconify icon="eva:file-text-outline" width={18} />
                {translateKey('petAi.documentSourcesLabel')}
              </Typography>
              <Stack spacing={1}>
                {uniqueSources.map((source, idx) => (
                  <Box
                    key={idx}
                    onClick={async (e) => {
                      e.preventDefault();
                      const fileUrl = source.fileUrl || '';
                      if (fileUrl.startsWith('http')) {
                        window.open(fileUrl, '_blank');
                      } else {
                        try {
                          const signedUrl = await getFileUrl({
                            kind: 'profileFile',
                            profileId,
                            filename: fileUrl,
                          });
                          window.open(signedUrl, '_blank');
                        } catch (error) {
                          console.error('Error fetching signed URL:', error);
                          enqueueSnackbar(translateKey('petAi.fileOpenError'), {
                            variant: 'error',
                          });
                        }
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: theme.palette.background.paper,
                      textDecoration: 'none',
                      color: theme.palette.text.primary,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: theme.palette.grey[100],
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <Iconify
                      icon="eva:file-outline"
                      width={20}
                      sx={{ color: theme.palette.primary.main }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {(source as any).originalFilename ||
                        source.filename ||
                        translateKey('visit.visitCourseMenuDocument')}
                    </Typography>
                    <Iconify
                      icon="eva:external-link-outline"
                      width={16}
                      sx={{ color: theme.palette.text.secondary }}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          );
        })()}
    </Stack>
  );

  // Skeleton styles for loading state
  const skeletonStyle = {
    bgcolor: 'text.primary',
    opacity: 0.2,
    '&::after': {
      background:
        'linear-gradient(90deg, transparent, rgba(181, 192, 201, 0.57), transparent)',
    },
  };

  const userName = userData
    ? `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`.trim()
    : user?.displayName || '';

  // One bubble row, kept visually identical to the Transcription tab
  // (TranscriptChatRow): 36px avatar, a "name · HH:mm:ss" caption header, then a
  // rounded z1-shadow bubble. "me" (right) in primary.lighter, assistant (left)
  // in grey.
  // Copy an assistant message into the clipboard as rich HTML (converted from
  // the same Markdown the editor uses), so a professional can paste it into a
  // Notatka editor with formatting preserved instead of raw Markdown.
  const handleCopyNote = async (markdown: string) => {
    const html = markdownToEditorHtml(markdown);
    try {
      const ClipboardItemCtor: any = (window as any).ClipboardItem;
      if (navigator.clipboard?.write && ClipboardItemCtor) {
        await navigator.clipboard.write([
          new ClipboardItemCtor({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([markdown], { type: 'text/plain' }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(markdown);
      }
      enqueueSnackbar(translateKey('hardcoded.skopiowano_do_schowka'), {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar(translateKey('hardcoded.nie_udalo_sie_skopiowac'), {
        variant: 'error',
      });
    }
  };

  const renderBubbleRow = (
    side: 'left' | 'right',
    name: string,
    time: string | Date | null | undefined,
    content: ReactNode,
    fullWidth = false,
    copyMarkdown?: string,
  ) => {
    const isRight = side === 'right';
    const formattedTime = formatChatTime(time);
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          alignSelf: isRight ? 'flex-end' : 'flex-start',
          // Most bubbles hug their content; the "thinking" skeleton spans the
          // full chat width. The cap only exists to leave a visible margin on
          // the opposite side, which is what tells the two speakers apart — 5%
          // reads as clearly as a wider one did and gives the text the rest.
          maxWidth: fullWidth ? '100%' : '95%',
          width: fullWidth ? '100%' : undefined,
          flexDirection: isRight ? 'row-reverse' : 'row',
        }}
      >
        {isRight ? (
          <CustomAvatar
            src={userData?.avatar || user?.photoURL}
            alt={userName}
            name={userName}
            sx={{ width: 36, height: 36, flexShrink: 0 }}
          />
        ) : (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: '#052330',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src="/assets/logo/logo_symbol.svg"
              alt="Vetsi"
              sx={{ width: 20, height: 20 }}
            />
          </Box>
        )}
        <Stack
          sx={{
            gap: 0.5,
            minWidth: 0,
            flex: fullWidth ? 1 : undefined,
            alignItems: fullWidth
              ? 'stretch'
              : isRight
              ? 'flex-end'
              : 'flex-start',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              {name}
              {formattedTime ? ` · ${formattedTime}` : ''}
            </Typography>
            {!isRight && copyMarkdown ? (
              <Tooltip title={translateKey('hardcoded.kopiuj')}>
                <IconButton
                  disableRipple
                  onClick={() => handleCopyNote(copyMarkdown)}
                  sx={{ color: 'text.secondary', p: 0, fontSize: 14 }}
                >
                  <Iconify icon="eva:copy-outline" width={14} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 2,
              backgroundColor: isRight
                ? 'primary.lighter'
                : theme.palette.grey[100],
              boxShadow: theme.customShadows.z1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              minWidth: 0,
            }}
          >
            {content}
          </Box>
        </Stack>
      </Box>
    );
  };

  // Message blocks — shared between the default (newest-first) layout and the
  // modal's natural (oldest-first) layout. Within an exchange the user's
  // question and the assistant's answer swap order to match.
  const summaryBlock =
    initialSummary && !isInitialLoading
      ? renderBubbleRow(
          'left',
          'Asystent Vetsi',
          initialSummary.generatedAt,
          <>
            <AssistantMarkdown>{initialSummary.content}</AssistantMarkdown>
            {visitId &&
              (!!initialSummary.differentialDiagnoses?.length ||
                !!initialSummary.suggestedTests?.length ||
                !!initialSummary.treatmentRecommendations?.length ||
                !!initialSummary.warnings?.length) &&
              renderStructuredData(initialSummary)}
          </>,
          false,
          initialSummary.content,
        )
      : null;

  const renderExchanges = (list: Exchange[]) =>
    list.map((exchange, idx) => {
      const userBubble = renderBubbleRow(
        'right',
        userName,
        exchange.askedAt,
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {exchange.question}
        </Typography>,
      );
      const assistantBubble = renderBubbleRow(
        'left',
        'Asystent Vetsi',
        exchange.response.generatedAt ?? exchange.askedAt,
        <>
          <AssistantMarkdown>{exchange.response.content}</AssistantMarkdown>
          {visitId &&
            (exchange.response.differentialDiagnoses?.length ||
              exchange.response.suggestedTests?.length ||
              exchange.response.treatmentRecommendations?.length ||
              exchange.response.warnings?.length) &&
            renderStructuredData(exchange.response)}
        </>,
        false,
        exchange.response.content,
      );
      return (
        <Box
          key={idx}
          sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
        >
          {naturalOrder ? (
            <>
              {userBubble}
              {assistantBubble}
            </>
          ) : (
            <>
              {assistantBubble}
              {userBubble}
            </>
          )}
        </Box>
      );
    });

  const pendingBubbles = (() => {
    if (!pendingQuestion) return null;
    const userBubble = renderBubbleRow(
      'right',
      userName,
      pendingAskedAt,
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {pendingQuestion}
      </Typography>,
    );
    const loadingBubble = renderBubbleRow(
      'left',
      'Asystent Vetsi',
      pendingAskedAt,
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box
            component="img"
            src="/assets/logo/logo_symbol.svg"
            alt="AI Analysis"
            sx={{
              height: 20,
              animation: 'rotate 2s infinite',
              '@keyframes rotate': {
                '0%, 100%': { transform: 'rotate(0deg)' },
                '50%': { transform: 'rotate(360deg)' },
              },
            }}
          />
          <Typography variant="body2">
            {loadingMessages[loadingMessageIndex]}
          </Typography>
        </Box>
        <Skeleton variant="text" width="60%" height={24} sx={skeletonStyle} />
        <Skeleton variant="text" width="90%" sx={skeletonStyle} />
        <Skeleton variant="text" width="75%" sx={skeletonStyle} />
      </>,
      true,
    );
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {naturalOrder ? (
          <>
            {userBubble}
            {loadingBubble}
          </>
        ) : (
          <>
            {loadingBubble}
            {userBubble}
          </>
        )}
      </Box>
    );
  })();

  const inputBox = (
    <Box sx={{ position: 'relative', zIndex: 2 }}>
      <TextField
        fullWidth
        placeholder={placeholder}
        value={question}
        size="medium"
        onChange={(e) => setQuestion(e.target.value)}
        onKeyPress={handleKeyPress}
        disabled={isLoading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <LoadingButton
                onClick={handleSubmitQuestion}
                disabled={isLoading || !question.trim()}
                loading={isLoading}
              >
                <Iconify
                  icon="eva:paper-plane-fill"
                  color={
                    question.trim()
                      ? theme.palette.text.primary
                      : theme.palette.text.disabled
                  }
                  width={24}
                  height={24}
                />
              </LoadingButton>
            </InputAdornment>
          ),
          sx: {
            pr: 3,
          },
        }}
      />
      <Box sx={{ mt: 1, display: 'flex' }}>
        <Button
          onClick={handleNewChat}
          startIcon={<Iconify icon="eva:plus-circle-outline" width={16} />}
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            fontWeight: 400,
            fontSize: 13,
            px: 1.5,
            minWidth: 0,
          }}
        >
          Rozpocznij nowy czat
        </Button>
      </Box>
    </Box>
  );

  const messagesBox = (
    <Box
      ref={scrollContainerRef}
      sx={{
        ...(fillHeight ? { flex: 1, minHeight: 0 } : { maxHeight: '60vh' }),
        overflowY: 'auto',
        // edgeScrollbar: push the scroll viewport's right edge out into the
        // surrounding container's existing gutter and pad the content back the
        // same amount, so the scrollbar lands at the panel edge — beside the
        // right-aligned avatars — without shifting content or adding any new
        // whitespace. The pull has to be the wrapper's real gutter: pulling
        // further than it has leaves the viewport past the panel's edge and the
        // panel scrolls sideways. Otherwise keep the original minimal gutter.
        ...(edgeScrollbar
          ? { mr: -edgeScrollbarGutter, pr: edgeScrollbarGutter }
          : { pr: 0.5 }),
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {naturalOrder ? (
        <>
          {summaryBlock}
          {renderExchanges(exchanges)}
          {pendingBubbles}
        </>
      ) : (
        <>
          {pendingBubbles}
          {renderExchanges([...exchanges].reverse())}
          {summaryBlock}
        </>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        ...(fillHeight && { height: '100%', minHeight: 0 }),
      }}
    >
      {naturalOrder ? (
        <>
          {messagesBox}
          {inputBox}
        </>
      ) : (
        <>
          {inputBox}
          {messagesBox}
        </>
      )}
    </Box>
  );
}
